import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LicenseInfo } from '@/services/licenseService';

export type LicenseState = 'valid' | 'verification_needed' | 'expired' | 'verifying' | 'offline_mode';

// Raw license info from Tauri (nested structure with snake_case)
interface TauriLicenseInfo {
  license: {
    id: string;
    company_id: string;
    license_key: string;
    license_type: string;
    status: string;
    device_fingerprint?: string;
    device_name?: string;
    os_info?: string;
    activation_date?: string;
    expires_at?: string;
    last_verified_at?: string;
    max_devices: number;
    grace_period_days: number;
    created_at: string;
  };
  needs_verification: boolean;
  in_grace_period: boolean;
  grace_days_remaining?: number;
  days_until_expiry?: number;
}

// Type guard to check if it's a Tauri nested structure
function isTauriLicenseInfo(obj: any): obj is TauriLicenseInfo {
  return obj && typeof obj === 'object' && 'license' in obj && typeof obj.license === 'object' && 'license_key' in obj.license;
}

// Normalize Tauri license to flat LicenseInfo
function normalizeLicenseInfo(input: LicenseInfo | TauriLicenseInfo): LicenseInfo {
  if (isTauriLicenseInfo(input)) {
    // Tauri nested structure - flatten it
    return {
      ...input.license,
      needsVerification: input.needs_verification,
      inGracePeriod: input.in_grace_period,
      graceDaysRemaining: input.grace_days_remaining,
      daysUntilExpiry: input.days_until_expiry,
    } as LicenseInfo;
  }
  // Already flat LicenseInfo
  return input;
}

interface LicenseStore {
  // State
  licenseState: LicenseState;
  license: LicenseInfo | null;
  lastVerifiedAt?: string;
  verificationDueAt?: string;
  gracePeriodEndsAt?: string;
  isBlocked: boolean;

  // Actions
  setLicenseState: (state: LicenseState) => void;
  setLicense: (licenseInfo: LicenseInfo | TauriLicenseInfo | null) => void;
  setLastVerifiedAt: (date: string) => void;
  setVerificationDueAt: (date: string) => void;
  setGracePeriodEndsAt: (date?: string) => void;
  setBlocked: (blocked: boolean) => void;
  reset: () => void;

  // Computed
  needsVerification: () => boolean;
  isInGracePeriod: () => boolean;
  daysUntilExpiry: () => number | null;
  graceDaysRemaining: () => number | null;
}

export const useLicenseStore = create<LicenseStore>()(
  persist(
    (set, get) => ({
      // Initial state
      licenseState: 'verification_needed',
      license: null,
      lastVerifiedAt: undefined,
      verificationDueAt: undefined,
      gracePeriodEndsAt: undefined,
      isBlocked: false,

      // Actions
      setLicenseState: (licenseState) => set({ licenseState }),

      setLicense: (licenseInfo) => {
        if (!licenseInfo) {
          set({ license: null });
          return;
        }

        // Normalize from Tauri nested structure to flat LicenseInfo
        const license = normalizeLicenseInfo(licenseInfo);
        set({ license });

        // Update verification dates based on license
        const lastVerified = license.last_verified_at || new Date().toISOString();
        const verificationDue = new Date(lastVerified);
        verificationDue.setDate(verificationDue.getDate() + 30);

        const gracePeriodEnds = new Date(verificationDue);
        gracePeriodEnds.setDate(gracePeriodEnds.getDate() + (license.grace_period_days || 7));

        set({
          lastVerifiedAt: lastVerified,
          verificationDueAt: verificationDue.toISOString(),
          gracePeriodEndsAt: gracePeriodEnds.toISOString(),
        });

        // Determine state
        if (license.status === 'expired') {
          set({ licenseState: 'expired', isBlocked: true });
        } else if (license.status === 'revoked') {
          set({ licenseState: 'expired', isBlocked: true });
        } else if (license.needsVerification) {
          if (license.inGracePeriod) {
            set({ licenseState: 'verification_needed', isBlocked: false });
          } else {
            // Grace period ended
            set({ licenseState: 'expired', isBlocked: true });
          }
        } else {
          set({ licenseState: 'valid', isBlocked: false });
        }
      },

      setLastVerifiedAt: (lastVerifiedAt) => set({ lastVerifiedAt }),

      setVerificationDueAt: (verificationDueAt) => set({ verificationDueAt }),

      setGracePeriodEndsAt: (gracePeriodEndsAt) => set({ gracePeriodEndsAt }),

      setBlocked: (isBlocked) => set({ isBlocked }),

      reset: () =>
        set({
          licenseState: 'verification_needed',
          license: null,
          lastVerifiedAt: undefined,
          verificationDueAt: undefined,
          gracePeriodEndsAt: undefined,
          isBlocked: false,
        }),

      // Computed
      needsVerification: () => {
        const { license } = get();
        return license?.needsVerification || false;
      },

      isInGracePeriod: () => {
        const { license } = get();
        return license?.inGracePeriod || false;
      },

      daysUntilExpiry: () => {
        const { license } = get();
        return license?.daysUntilExpiry !== undefined ? license.daysUntilExpiry : null;
      },

      graceDaysRemaining: () => {
        const { license } = get();
        return license?.graceDaysRemaining !== undefined ? license.graceDaysRemaining : null;
      },
    }),
    {
      name: 'license-store',
      partialize: (state) => ({
        license: state.license,
        lastVerifiedAt: state.lastVerifiedAt,
        verificationDueAt: state.verificationDueAt,
        gracePeriodEndsAt: state.gracePeriodEndsAt,
      }),
    }
  )
);
